<template>
  <b-modal
    v-model="show"
    centered
    scrollable
    modal-class="lz-activity-modal lz-controls"
    content-class="lz-modal-content"
    body-class="lz-modal-body"
    size="lg"
    title="Mandatory Leave"
    header-bg-variant="white"
    header-text-variant="dark"
    body-bg-variant="white"
    body-text-variant="dark"
    footer-bg-variant="white"
    @show="onShow">
    <div class="mandatory-toolbar">
      <span class="mandatory-toolbar-label">Year</span>
      <b-form-radio-group
        v-model="year"
        :options="yearOptions"
        class="mandatory-year-switch"
        button-variant="outline-secondary"
        name="mandatory-leave-years"
        size="sm"
        buttons
      ></b-form-radio-group>
    </div>

    <div v-if="loading">
      <b-skeleton class="mb-3" height="120px" v-for="n in 2" :key="n"></b-skeleton>
    </div>
    <b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>
    <b-alert v-else-if="!periods.length" show variant="secondary">
      No mandatory leave periods configured for {{ year }}. Add MANDATORY holiday
      records to the system user to define them.
    </b-alert>
    <div v-else>
      <div
        v-for="period in periods"
        :key="period.definitionUid"
        class="mandatory-period">
        <div class="mandatory-period-head">
          <span class="mandatory-period-dates">
            <b-icon icon="calendar3" aria-hidden="true"></b-icon>
            {{ formatDate(period.startDate) }} to {{ formatDate(period.endDate) }}
          </span>
          <span class="mandatory-period-meta">
            {{ period.totalDays }} working day{{ period.totalDays !== 1 ? 's' : '' }}
          </span>
          <span
            class="mandatory-period-count"
            :class="{ 'is-complete': coveredCount(period) === allUsers(period).length }">
            {{ coveredCount(period) }} of {{ allUsers(period).length }} added
          </span>
        </div>

        <div class="mandatory-user-list">
          <div
            v-for="user in allUsers(period)"
            :key="user.uid"
            class="mandatory-user-row">
            <span class="user-name">{{ user.firstName }} {{ user.lastName }}</span>
            <span class="user-email">{{ user.email }}</span>
            <span v-if="user.covered" class="user-covered">
              <b-icon icon="check2" aria-hidden="true"></b-icon>
              Added
            </span>
            <b-button
              v-else-if="canApply"
              size="sm"
              variant="outline-primary"
              class="user-add-btn"
              :disabled="!!applying[period.definitionUid + '-' + user.uid]"
              @click="onApplyUser(period, user)">
              <b-spinner small v-if="applying[period.definitionUid + '-' + user.uid]"></b-spinner>
              <span v-else>Add</span>
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <b-button
        size="sm"
        variant="outline-secondary"
        class="lz-close-btn"
        @click="show = false">
        Close
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { format as dateFormat } from 'date-fns'
import ServicesOld from '../../../services/index.vue'

export default {
  name: 'MandatoryLeaveModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    canApply: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      year: currentYear,
      loading: false,
      error: null,
      periods: [],
      applying: {},
      yearOptions: [currentYear - 1, currentYear, currentYear + 1].map((y) => ({
        value: y,
        text: String(y)
      }))
    }
  },
  computed: {
    show: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    year() {
      this.fetchPeriods()
    }
  },
  methods: {
    allUsers(period) {
      return [...(period.coveredUsers ?? []), ...(period.uncoveredUsers ?? [])]
        .sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()
          return nameA < nameB ? -1 : nameA > nameB ? 1 : 0
        })
    },
    coveredCount(period) {
      return (period.coveredUsers ?? []).length
    },
    formatDate(date) {
      if (!date) return 'n/a'
      const d = new Date(date)
      return isNaN(d.getTime()) ? 'n/a' : dateFormat(d, 'dd/MM/yyyy')
    },
    async onApplyUser(period, user) {
      const key = period.definitionUid + '-' + user.uid
      this.$set(this.applying, key, true)
      try {
        await ServicesOld.ApplyMandatoryLeave({
          year: this.year,
          managerId: this.$store.getters.userAuth.details.uid,
          userUid: user.uid,
          comment: period.label,
          dryRun: false
        })
        this.$toasted.success(`Mandatory leave added for ${user.firstName} ${user.lastName}.`)
        await this.fetchPeriods()
      } catch {
        this.$toasted.error(`Failed to add mandatory leave for ${user.firstName} ${user.lastName}.`)
      } finally {
        this.$set(this.applying, key, false)
      }
    },
    onShow() {
      this.fetchPeriods()
    },
    async fetchPeriods() {
      this.loading = true
      this.error = null
      this.periods = []
      try {
        const { status, data } = await ServicesOld.GetMandatoryLeave({ year: this.year })
        if (status === 200) {
          this.periods = (data.content?.mandatoryDates ?? []).map((period) => ({
            ...period,
            coveredUsers: (period.coveredUsers ?? []).map((u) => ({ ...u, covered: true })),
            uncoveredUsers: (period.uncoveredUsers ?? []).map((u) => ({ ...u, covered: false }))
          }))
        }
      } catch (err) {
        console.error('[MandatoryLeaveModal] fetchPeriods error:', err)
        this.error = 'Failed to load mandatory leave periods. Please try again.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.mandatory-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.mandatory-toolbar-label {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

/* Segmented control. The labels are rendered by b-form-radio-group, so they
   sit outside this component's scope and need the deep combinator. */
.mandatory-year-switch {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.mandatory-year-switch >>> .btn {
  margin: 0 !important;
  border: none !important;
  border-radius: 8px !important;
  background-color: transparent !important;
  padding: 5px 16px;
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b !important;
  box-shadow: none !important;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}
.mandatory-year-switch >>> .btn:hover:not(.active) {
  background-color: rgba(148, 163, 184, 0.16) !important;
  color: #334155 !important;
}
.mandatory-year-switch >>> .btn.active {
  background-color: #ffffff !important;
  color: #0073be !important;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12) !important;
}
.mandatory-year-switch >>> .btn.focus {
  box-shadow: 0 0 0 3px rgba(0, 115, 190, 0.15) !important;
}
.mandatory-period {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
}
.mandatory-period:last-child {
  margin-bottom: 0;
}
.mandatory-period-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 14px;
  padding: 12px 16px;
  border-bottom: 1px solid #eef0f2;
  background-color: #f8fafc;
}
.mandatory-period-dates {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 700;
  color: #0073be;
}
.mandatory-period-meta {
  font-size: 12.5px;
  color: #64748b;
}
.mandatory-period-count {
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  color: #b45309;
  background-color: rgba(217, 119, 6, 0.1);
  border-radius: 999px;
  padding: 3px 10px;
}
.mandatory-period-count.is-complete {
  color: #15803d;
  background-color: rgba(22, 163, 74, 0.1);
}
.mandatory-user-list {
  max-height: 260px;
  overflow-y: auto;
}
.mandatory-user-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  font-size: 14px;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
  min-height: 44px;
}
.mandatory-user-row:last-child {
  border-bottom: none;
}
.mandatory-user-row:hover {
  background-color: #f8fafc;
}
.user-name {
  width: 180px;
  flex-shrink: 0;
  font-weight: 600;
}
.user-email {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-covered {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 600;
  color: #15803d;
}
.user-add-btn {
  border-radius: 8px;
  font-weight: 600;
  padding: 3px 14px;
}
</style>
